import { useState, useEffect, useMemo } from 'react';
import { PmCard, PmColumnPicker, PmHeadCell, useTableSortFilter, handleInlineEditKeyDown, type SortFilterColumn } from '../../../components/pm';
import {
  Box, Typography, Chip, IconButton, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Stack,
  Dialog, DialogContent, DialogActions, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Slider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import {
  useStakeholders, useCreateStakeholder, useUpdateStakeholder, useDeleteStakeholder,
} from '../../../api/hooks/useStakeholders';
import { useProject } from '../../../api/hooks/useProjects';
import { useNotifications } from '../../../context/NotificationContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { useAutosave } from '../../../hooks/useAutosave';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildStakeholdersReport } from '../reports/stakeholdersReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';
import { StakeholderGraph } from '../components/StakeholderGraph';

interface Stakeholder {
  id: string;
  name: string;
  role: string | null;
  type: 'group' | 'person' | 'extern';
  power: number;
  interest: number;
  strategy: string | null;
  strategy_override: boolean;
  notes: string | null;
  color?: string | null;
  categories?: string[] | null;
}

const TYPES: Stakeholder['type'][] = ['group', 'person', 'extern'];
const STRATEGIES = ['Manage closely', 'Keep satisfied', 'Keep informed', 'Monitor'] as const;
const COLORS = ['k01', 'k02', 'k03', 'k04', 'k05', 'k06', 'k07', 'k08', 'k09', 'k10', 'k11', 'k12'] as const;
const LEVELS = [1, 2, 3, 4, 5];

const parseCategories = (raw: string): string[] =>
  raw.split(',').map((s) => s.trim()).filter(Boolean);

interface CoreFields {
  name: string;
  role: string;
  type: Stakeholder['type'];
  power: number;
  interest: number;
}

export function StakeholdersTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data, isLoading } = useStakeholders(projectId);
  const createMut = useCreateStakeholder(projectId);
  const updateMut = useUpdateStakeholder(projectId);
  const deleteMut = useDeleteStakeholder(projectId);
  const items = ((data as unknown as { data?: Stakeholder[] } | undefined)?.data ?? []);

  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  // Dialog dient jetzt nur noch der Detailbearbeitung (Farbe/Strategie/Kategorien/Notizen).
  const [editItem, setEditItem] = useState<Stakeholder | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '', role: '', type: 'person' as Stakeholder['type'],
    power: 3, interest: 3, strategy_override: false,
    strategy: '' as string, notes: '', color: 'k02', categoriesRaw: '',
  });

  useEffect(() => {
    if (!editItem) return;
    setForm({
      name: editItem.name, role: editItem.role ?? '',
      type: editItem.type, power: editItem.power, interest: editItem.interest,
      strategy_override: editItem.strategy_override,
      strategy: editItem.strategy ?? '', notes: editItem.notes ?? '',
      color: editItem.color ?? 'k02',
      categoriesRaw: (editItem.categories ?? []).join(', '),
    });
  }, [editItem]);

  const handleDialogSave = async () => {
    if (!editItem) return;
    const cats = parseCategories(form.categoriesRaw);
    const payload: Record<string, unknown> = {
      name: form.name.trim(), role: form.role.trim() || null, type: form.type,
      power: form.power, interest: form.interest, strategy_override: form.strategy_override,
      notes: form.notes.trim() || null, color: form.color || null, categories: cats,
    };
    if (form.strategy_override && form.strategy) payload.strategy = form.strategy;
    try {
      await updateMut.mutateAsync({ id: editItem.id, data: payload });
      notify(tc('status.saved', 'Gespeichert'), 'success');
      setEditItem(null);
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteId(null); }
  };

  const typeLabel = (tp: Stakeholder['type']) => t(`stakeholders.types.${tp}`, tp);

  const columns = useMemo<SortFilterColumn<Stakeholder>[]>(() => [
    {
      key: 'name', label: t('fields.name', 'Name'), hideable: false,
      filter: { type: 'text' }, value: (r) => r.name,
    },
    {
      key: 'role', label: t('stakeholders.role', 'Rolle'), hideable: false,
      filter: { type: 'text' }, value: (r) => r.role ?? '',
    },
    {
      key: 'type', label: t('stakeholders.type', 'Typ'), hideable: false,
      filter: { type: 'enum', options: TYPES.map((tp) => ({ value: tp, label: typeLabel(tp) })) },
      value: (r) => r.type,
    },
    { key: 'power', label: t('stakeholders.power', 'Power'), align: 'center', hideable: false, value: (r) => r.power },
    { key: 'interest', label: t('stakeholders.interest', 'Interest'), align: 'center', hideable: false, value: (r) => r.interest },
    {
      key: 'strategy', label: t('stakeholders.strategy', 'Strategie'), defaultHidden: true,
      filter: { type: 'enum', options: STRATEGIES.map((s) => ({ value: s, label: s })) },
      value: (r) => r.strategy ?? '',
    },
    {
      key: 'categories', label: t('stakeholders.categories', 'Kategorien'), defaultHidden: true,
      filter: { type: 'text' }, value: (r) => (r.categories ?? []).join(', '),
    },
  ], [t]);

  const sf = useTableSortFilter(items, columns, { tableId: 'stakeholders' });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const head = { color: 'var(--pm-fg-subtle)', fontWeight: 600 };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('stakeholders.title', 'Stakeholder') };
  const buildReport = () =>
    buildStakeholdersReport({
      items: items.map((s) => ({
        name: s.name, role: s.role, type: s.type, power: s.power, interest: s.interest,
      })),
    });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{t('stakeholders.title', 'Stakeholder')}</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PmColumnPicker columns={sf.hideableColumns} isVisible={sf.isVisible} onToggle={sf.toggleColumn} />
          <PdfExportButton meta={pdfMeta} build={buildReport} />
        </Stack>
      </Stack>

      {/* Power/Interest-Matrix (Punkte ziehbar); speichert nur power/interest, Rest unverändert */}
      <PmCard sx={{ mb: 2, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('stakeholderMatrix.title', 'Stakeholder-Matrix')}</Typography>
        <StakeholderGraph
          items={items.map((s) => ({ id: s.id, name: s.name, power: s.power, interest: s.interest, color: s.color }))}
          onDropPoint={async (id, pos) => {
            const s = items.find((x) => x.id === id);
            if (!s) return;
            const payload: Record<string, unknown> = {
              name: s.name, role: s.role, type: s.type,
              power: pos.power, interest: pos.interest,
              strategy_override: s.strategy_override, notes: s.notes,
              color: s.color ?? null, categories: s.categories ?? [],
            };
            if (s.strategy_override && s.strategy) payload.strategy = s.strategy;
            try { await updateMut.mutateAsync({ id, data: payload }); }
            catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
          }}
        />
      </PmCard>

      <PmCard flush>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--pm-bg-muted)' }}>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            {t('stakeholders.inlineHint', 'Oben direkt eintragen und Enter drücken. Power/Interest 1–5; Farbe, Strategie und Kategorien über das Stift-Symbol.')}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {sf.visibleColumns.map((col) => (
                <PmHeadCell
                  key={col.key}
                  label={col.label}
                  align={col.align}
                  filter={col.filter}
                  sortActive={sf.sortKey === col.key}
                  sortDir={sf.sortDir}
                  onToggleSort={() => sf.toggleSort(col.key)}
                  filterValue={sf.filters[col.key]}
                  onSetFilter={(v) => sf.setFilter(col.key, v)}
                  onClearFilter={() => sf.clearFilter(col.key)}
                  headSx={head}
                />
              ))}
              <TableCell sx={head} align="right">{tc('actions.actions', 'Aktionen')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <AddStakeholderRow
              isVisible={sf.isVisible}
              typeLabel={(tp) => t(`stakeholders.types.${tp}`, tp)}
              onCreate={async (core) => {
                await createMut.mutateAsync({
                  name: core.name, role: core.role || null, type: core.type,
                  power: core.power, interest: core.interest,
                  strategy_override: false, notes: null, color: 'k02', categories: [],
                });
              }}
            />
            {sf.rows.map((s) => (
              <StakeholderRow
                key={s.id}
                member={s}
                isVisible={sf.isVisible}
                typeLabel={(tp) => t(`stakeholders.types.${tp}`, tp)}
                onSave={async (core) => {
                  const payload: Record<string, unknown> = {
                    name: core.name, role: core.role || null, type: core.type,
                    power: core.power, interest: core.interest,
                    strategy_override: s.strategy_override, notes: s.notes,
                    color: s.color ?? null, categories: s.categories ?? [],
                  };
                  if (s.strategy_override && s.strategy) payload.strategy = s.strategy;
                  await updateMut.mutateAsync({ id: s.id, data: payload });
                }}
                onEditDetail={() => setEditItem(s)}
                onDelete={() => setDeleteId(s.id)}
              />
            ))}
            {sf.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={sf.visibleColumns.length + 1}>
                  <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                    {items.length === 0
                      ? t('stakeholders.empty', 'Noch keine Stakeholder erfasst — oben in der ersten Zeile eintragen.')
                      : t('stakeholders.filterEmpty', 'Keine Treffer fuer die aktuelle Filter-Kombination.')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </PmCard>

      {/* Detail-Dialog (Farbe, Strategie, Kategorien, Notizen + alle Kernfelder) */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{t('stakeholders.editTitle', 'Stakeholder bearbeiten')}</AppDialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField required label={t('fields.name', 'Name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField label={t('stakeholders.role', 'Rolle')} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel>{t('stakeholders.type', 'Typ')}</InputLabel>
              <Select value={form.type} label={t('stakeholders.type', 'Typ')} onChange={(e) => setForm({ ...form, type: e.target.value as Stakeholder['type'] })}>
                {TYPES.map((tp) => <MenuItem key={tp} value={tp}>{t(`stakeholders.types.${tp}`, tp)}</MenuItem>)}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'var(--pm-fg-subtle)' }}>
                {t('stakeholders.color', 'Wiedererkennungsfarbe')} <span style={{ fontSize: '0.7rem' }}>(RITTEC-Palette)</span>
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {COLORS.map((c) => {
                  const active = form.color === c;
                  return (
                    <Box
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      sx={{
                        width: 28, height: 28, borderRadius: '50%', background: `var(--pm-${c})`, cursor: 'pointer',
                        border: active ? '2px solid var(--pm-fg-base)' : '2px solid transparent',
                        outline: active ? '1px solid var(--pm-bg-muted)' : 'none',
                        transition: 'transform .12s', '&:hover': { transform: 'scale(1.1)' },
                      }}
                      title={c}
                    />
                  );
                })}
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption">{t('stakeholders.power', 'Power')} ({form.power})</Typography>
              <Slider value={form.power} onChange={(_, v) => setForm({ ...form, power: v as number })} min={1} max={5} step={1} marks />
            </Box>
            <Box>
              <Typography variant="caption">{t('stakeholders.interest', 'Interest')} ({form.interest})</Typography>
              <Slider value={form.interest} onChange={(_, v) => setForm({ ...form, interest: v as number })} min={1} max={5} step={1} marks />
            </Box>
            <FormControl fullWidth>
              <InputLabel>{t('stakeholders.strategyOverride', 'Strategie (Override)')}</InputLabel>
              <Select
                value={form.strategy_override ? form.strategy : ''}
                label={t('stakeholders.strategyOverride', 'Strategie (Override)')}
                onChange={(e) => {
                  if (!e.target.value) setForm({ ...form, strategy_override: false, strategy: '' });
                  else setForm({ ...form, strategy_override: true, strategy: e.target.value });
                }}
              >
                <MenuItem value="">{t('stakeholders.autoStrategy', 'Auto (aus Power × Interest)')}</MenuItem>
                {STRATEGIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label={t('stakeholders.categories', 'Kategorien')}
              value={form.categoriesRaw}
              onChange={(e) => setForm({ ...form, categoriesRaw: e.target.value })}
              placeholder={t('stakeholders.categoriesPlaceholder', 'z.B. Sponsor, Hauptnutzer (komma-getrennt)')}
              helperText={t('stakeholders.categoriesHelper', 'Mehrere Tags mit Komma trennen')}
              fullWidth
            />
            {parseCategories(form.categoriesRaw).length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: -1.5 }}>
                {parseCategories(form.categoriesRaw).map((c) => <Chip key={c} size="small" label={c} variant="outlined" />)}
              </Stack>
            )}
            <TextField label={t('fields.notes', 'Notizen')} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline rows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={handleDialogSave} disabled={!form.name.trim() || updateMut.isPending}>
            {tc('actions.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        title={t('stakeholders.deleteTitle', 'Stakeholder löschen?')}
        message={t('stakeholders.deleteConfirm', 'Dieser Stakeholder wird endgültig entfernt.')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}

/** Inline-Anlegen direkt in der ersten Zeile (Name Pflicht, Enter legt an). */
function AddStakeholderRow({
  onCreate, typeLabel, isVisible,
}: {
  isVisible: (k: string) => boolean;
  onCreate: (core: CoreFields) => Promise<void>;
  typeLabel: (tp: Stakeholder['type']) => string;
}) {
  const { t } = useTranslation('projects');
  const EMPTY: CoreFields = { name: '', role: '', type: 'person', power: 3, interest: 3 };
  const [form, setForm] = useState<CoreFields>(EMPTY);
  const [busy, setBusy] = useState(false);
  const canCreate = form.name.trim().length > 0;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    try { await onCreate({ ...form, name: form.name.trim(), role: form.role.trim() }); setForm(EMPTY); }
    finally { setBusy(false); }
  };
  const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } };

  return (
    <TableRow sx={{ bgcolor: 'var(--pm-bg-subtle)' }}>
      <TableCell>
        <TextField variant="standard" fullWidth autoFocus placeholder={t('stakeholders.namePlaceholder', 'Name eingeben…')}
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth placeholder={t('stakeholders.role', 'Rolle')}
          value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <Select variant="standard" fullWidth value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as Stakeholder['type'] })}>
          {TYPES.map((tp) => <MenuItem key={tp} value={tp}>{typeLabel(tp)}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell align="center">
        <Select variant="standard" value={form.power} onChange={(e) => setForm({ ...form, power: Number(e.target.value) })}>
          {LEVELS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell align="center">
        <Select variant="standard" value={form.interest} onChange={(e) => setForm({ ...form, interest: Number(e.target.value) })}>
          {LEVELS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </TableCell>
      {(isVisible('strategy') || isVisible('categories')) && (
        <TableCell colSpan={Number(isVisible('strategy')) + Number(isVisible('categories'))}>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
            {t('stakeholders.detailAfterCreate', 'Strategie & Kategorien nach dem Anlegen per Stift')}
          </Typography>
        </TableCell>
      )}
      <TableCell align="right">
        <IconButton color="primary" onClick={() => void submit()} disabled={!canCreate || busy} aria-label={t('stakeholders.add', 'Neuer Stakeholder')}>
          <AddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

/** Bestehende Zeile — Kernfelder inline editierbar (Autosave), Detail via Stift. */
function StakeholderRow({
  member, onSave, onEditDetail, onDelete, typeLabel, isVisible,
}: {
  isVisible: (k: string) => boolean;
  member: Stakeholder;
  onSave: (core: CoreFields) => Promise<void>;
  onEditDetail: () => void;
  onDelete: () => void;
  typeLabel: (tp: Stakeholder['type']) => string;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');

  const [form, setForm] = useState<CoreFields>({
    name: member.name, role: member.role ?? '', type: member.type, power: member.power, interest: member.interest,
  });

  const autosave = useAutosave<CoreFields>({
    canSave: (f) => f.name.trim().length > 0,
    onSave: async (f) => { await onSave({ ...f, name: f.name.trim(), role: f.role.trim() }); },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setForm({ name: member.name, role: member.role ?? '', type: member.type, power: member.power, interest: member.interest });
  }, [autosave.isDirty, member.name, member.role, member.type, member.power, member.interest]);

  const update = (patch: Partial<CoreFields>) => { const next = { ...form, ...patch }; setForm(next); autosave.schedule(next); };
  const onKeyDown = (e: React.KeyboardEvent) => handleInlineEditKeyDown(e, autosave.flush);

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: `var(--pm-${member.color ?? 'k02'})`, border: '1px solid var(--pm-bg-muted)', flexShrink: 0 }} />
          <TextField variant="standard" fullWidth value={form.name} onChange={(e) => update({ name: e.target.value })} onKeyDown={onKeyDown} />
        </Stack>
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth value={form.role} placeholder={t('stakeholders.role', 'Rolle')}
          onChange={(e) => update({ role: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <Select variant="standard" fullWidth value={form.type} onChange={(e) => update({ type: e.target.value as Stakeholder['type'] })}>
          {TYPES.map((tp) => <MenuItem key={tp} value={tp}>{typeLabel(tp)}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell align="center">
        <Select variant="standard" value={form.power} onChange={(e) => update({ power: Number(e.target.value) })}>
          {LEVELS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell align="center">
        <Select variant="standard" value={form.interest} onChange={(e) => update({ interest: Number(e.target.value) })}>
          {LEVELS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </TableCell>
      {isVisible('strategy') && (
        <TableCell><Typography variant="body2" sx={{ color: 'var(--pm-fg-muted)' }}>{member.strategy ?? '—'}</Typography></TableCell>
      )}
      {isVisible('categories') && (
        <TableCell>
          {member.categories && member.categories.length > 0 ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {member.categories.map((c) => <Chip key={c} size="small" label={c} variant="outlined" />)}
            </Stack>
          ) : '—'}
        </TableCell>
      )}
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} compact />
          <IconButton size="small" onClick={onEditDetail} aria-label={tc('actions.edit', 'Bearbeiten')}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={onDelete} aria-label={tc('actions.delete', 'Löschen')}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
